// "¿Dónde está tu obra?": el cliente ubica su obra solo (buscador + mapa con pin), sin pedir soporte.
// Se guarda en la obra de la cámara (Firebase, ubicacionMaps) y de ahí lo toman Clima, Dashboard IA y Calendario.
import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Search, Crosshair, X, Loader2 } from 'lucide-react';
import { searchPlace, searchAddress, saveObraLocation } from '../../camera/components/IADashboard/weather';

const AR = { lat: -34.6037, lng: -58.3816 }; // si no hay nada: Buenos Aires, alejado
const fmt = (n) => (Math.round(n * 1e5) / 1e5).toFixed(5);

const pinIcon = L.divIcon({
  className: 'ubi-pin',
  html: '<svg width="34" height="44" viewBox="0 0 34 44" xmlns="http://www.w3.org/2000/svg"><path d="M17 43s15-14.6 15-26A15 15 0 0 0 2 17c0 11.4 15 26 15 26z" fill="#2563eb" stroke="#fff" stroke-width="2.5"/><circle cx="17" cy="17" r="5.5" fill="#fff"/></svg>',
  iconSize: [34, 44],
  iconAnchor: [17, 43],
});

function MapaPin({ point, onMove, zoom }) {
  const box = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);
  const moveRef = useRef(onMove);
  moveRef.current = onMove;

  useEffect(() => {
    const m = L.map(box.current, { zoomControl: true, attributionControl: true, scrollWheelZoom: true, tap: true });
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a>',
    }).addTo(m);
    const start = point || AR;
    m.setView([start.lat, start.lng], point ? zoom || 16 : 4);
    m.on('click', (e) => moveRef.current({ lat: e.latlng.lat, lng: e.latlng.lng }));
    map.current = m;
    // El contenedor puede cambiar de tamaño (modal, celular): Leaflet tiene que volver a medir
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => m.invalidateSize()) : null;
    if (ro) ro.observe(box.current);
    const t = setTimeout(() => m.invalidateSize(), 150);
    return () => { clearTimeout(t); if (ro) ro.disconnect(); m.remove(); map.current = null; marker.current = null; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const m = map.current;
    if (!m || !point) return;
    if (!marker.current) {
      marker.current = L.marker([point.lat, point.lng], { draggable: true, icon: pinIcon, autoPan: true }).addTo(m);
      marker.current.on('dragend', () => { const p = marker.current.getLatLng(); moveRef.current({ lat: p.lat, lng: p.lng }); });
    } else {
      marker.current.setLatLng([point.lat, point.lng]);
    }
    if (point.fly) m.flyTo([point.lat, point.lng], point.zoom || 16, { duration: 0.6 });
  }, [point]);

  return <div ref={box} className="w-full h-64 sm:h-80 rounded-xl overflow-hidden border border-gray-200 z-0" style={{ background: '#e5e7eb' }} />;
}

/**
 * Selector de ubicación de la obra.
 * props: feedId, cam, initial ({lat,lng} o null), direccion (texto actual), onSaved(loc), onCancel (opcional), title
 */
export function UbicacionObraPicker({ feedId, cam, initial, direccion: direccionInicial, onSaved, onCancel, title }) {
  const [q, setQ] = useState('');
  const [sug, setSug] = useState(null); // resultados del buscador
  const [buscando, setBuscando] = useState(false);
  const [point, setPoint] = useState(initial && initial.lat != null ? { lat: +initial.lat, lng: +initial.lng } : null);
  const [direccion, setDireccion] = useState(direccionInicial || '');
  const [geo, setGeo] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const tick = useRef(0);
  const elegido = useRef(false); // al elegir un resultado el texto cambia: no volver a autocompletar

  // Autocompletado mientras escribe (localidades, Open-Meteo, el mismo servicio del clima)
  useEffect(() => {
    const t = q.trim();
    if (elegido.current) { elegido.current = false; return undefined; }
    if (t.length < 3) { setSug(null); return undefined; }
    const my = ++tick.current;
    const h = setTimeout(async () => {
      const r = await searchPlace(t).catch(() => []);
      if (my === tick.current) setSug(r);
    }, 350);
    return () => clearTimeout(h);
  }, [q]);

  // Enter / Buscar: dirección completa (calle y número) con OpenStreetMap, una búsqueda por pedido
  const buscarDireccion = async () => {
    const t = q.trim();
    if (t.length < 3) return;
    const my = ++tick.current;
    setBuscando(true);
    const [dirs, places] = await Promise.all([searchAddress(t).catch(() => []), searchPlace(t).catch(() => [])]);
    if (my !== tick.current) return;
    setBuscando(false);
    setSug([...dirs, ...places.filter((p) => !dirs.some((d) => Math.abs(d.lat - p.lat) < 1e-3 && Math.abs(d.lng - p.lng) < 1e-3))]);
  };

  const elegir = (r) => {
    setPoint({ lat: r.lat, lng: r.lng, fly: true, zoom: r.street ? 18 : 14 });
    setDireccion(r.label);
    setSug(null);
    tick.current += 1;
    elegido.current = true;
    setQ(r.label);
    setErr(null);
  };

  const miUbicacion = () => {
    if (!navigator.geolocation) { setErr('Tu navegador no permite usar la ubicación.'); return; }
    setGeo(true);
    setErr(null);
    navigator.geolocation.getCurrentPosition(
      (p) => { setGeo(false); setPoint({ lat: p.coords.latitude, lng: p.coords.longitude, fly: true, zoom: 18 }); },
      () => { setGeo(false); setErr('No pudimos tomar tu ubicación. Revisá el permiso del navegador o buscá la dirección.'); },
      { enableHighAccuracy: true, timeout: 12000 },
    );
  };

  const guardar = async () => {
    if (!point) return;
    setSaving(true);
    setErr(null);
    try {
      const loc = await saveObraLocation({ feedId, cam, lat: point.lat, lng: point.lng, direccion: direccion.trim() });
      onSaved && onSaved(loc);
    } catch (e) {
      const st = e?.response?.status;
      setErr(st === 403 ? 'Tu usuario no puede cambiar la ubicación de esta obra. Pedíselo al administrador de tu cuenta.'
        : 'No se pudo guardar la ubicación. Probá de nuevo en un rato.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 text-left">
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-10 h-10 rounded-full bg-blue-50 text-blue-600 grid place-items-center"><MapPin className="w-5 h-5" /></div>
        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-semibold text-gray-800 leading-tight">{title || '¿Dónde está tu obra?'}</h3>
          <p className="text-sm text-gray-600 mt-0.5">Buscá la dirección y ajustá el pin en el mapa. Con eso te mostramos el clima y la lluvia de tu obra.</p>
        </div>
        {onCancel && (
          <button type="button" onClick={onCancel} className="shrink-0 p-1.5 rounded-full text-gray-500 hover:bg-gray-100" aria-label="Cerrar"><X className="w-5 h-5" /></button>
        )}
      </div>

      <div className="relative">
        <div className="flex gap-2">
          <div className="relative flex-1 min-w-0">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onBlur={() => setTimeout(() => setSug(null), 200)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); buscarDireccion(); } if (e.key === 'Escape') setSug(null); }}
              placeholder="Calle y número, barrio o localidad"
              className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-gray-300 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              aria-label="Buscar dirección de la obra"
              autoComplete="off"
            />
          </div>
          <button type="button" onClick={buscarDireccion} disabled={q.trim().length < 3 || buscando}
            className="shrink-0 px-4 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
            {buscando ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Buscar'}
          </button>
        </div>
        {sug && (
          <ul className="absolute z-[1000] left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-auto">
            {sug.length ? sug.map((r) => (
              <li key={`${r.label}-${r.lat}-${r.lng}`}>
                <button type="button" onClick={() => elegir(r)} className="w-full text-left px-3 py-2.5 text-sm hover:bg-blue-50 flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5 text-gray-400 shrink-0" />
                  <span className="min-w-0"><span className="text-gray-800">{r.label}</span>{r.street && <span className="ml-1 text-xs text-blue-600">dirección</span>}</span>
                </button>
              </li>
            )) : <li className="px-3 py-2.5 text-sm text-gray-500">No encontramos ese lugar. Probá con calle y número + localidad, y apretá Buscar.</li>}
          </ul>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <button type="button" onClick={miUbicacion} disabled={geo} className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 disabled:opacity-60">
          {geo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crosshair className="w-4 h-4" />} Usar mi ubicación actual
        </button>
        <span className="text-xs text-gray-500">Útil si estás en la obra.</span>
      </div>

      <MapaPin point={point} onMove={(p) => { setPoint(p); setErr(null); }} />
      <p className="text-xs text-gray-500 -mt-1">
        {point ? <>Arrastrá el pin o tocá el mapa para marcar el punto exacto · {fmt(point.lat)}, {fmt(point.lng)}</> : 'Buscá la dirección o tocá el mapa donde está la obra.'}
      </p>

      <label className="block">
        <span className="text-xs font-medium text-gray-600">Dirección que se muestra (opcional)</span>
        <input value={direccion} onChange={(e) => setDireccion(e.target.value)} maxLength={300} placeholder="Ej.: Av. Corrientes 1234, CABA"
          className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
      </label>

      {err && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{err}</div>}

      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-1">
        {onCancel && <button type="button" onClick={onCancel} className="px-4 py-2.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancelar</button>}
        <button type="button" onClick={guardar} disabled={!point || saving}
          className="inline-flex justify-center items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Guardar ubicación
        </button>
      </div>
    </div>
  );
}

/** El mismo selector en una ventana, para "Cambiar ubicación". */
export function UbicacionObraModal({ open, onClose, ...props }) {
  useEffect(() => {
    if (!open) return undefined;
    const k = (e) => e.key === 'Escape' && onClose && onClose();
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[2000] bg-black/40 flex items-end sm:items-center justify-center sm:p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose && onClose()}>
      <div className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl shadow-xl p-4 sm:p-6 max-h-[92vh] overflow-y-auto" role="dialog" aria-modal="true" aria-label="Cambiar ubicación de la obra">
        <UbicacionObraPicker title="Cambiar ubicación de la obra" onCancel={onClose} {...props} />
      </div>
    </div>
  );
}

/** Link chico "Cambiar ubicación". */
export function CambiarUbicacionLink({ onClick, className = '', style }) {
  return (
    <button type="button" onClick={onClick} style={style}
      className={`inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline ${className}`}>
      <MapPin className="w-3.5 h-3.5" /> Cambiar ubicación
    </button>
  );
}
